'use client';

import type { ReactElement, ReactNode } from 'react';
import * as Popover from '@radix-ui/react-popover';
import { cn } from '@/utils/cn';

interface PopoverMenuProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  children: ReactNode;
  contentClassName?: string;
  trigger: ReactNode;
  renderAnchor?: (trigger: ReactNode) => ReactNode;
  disableClickOutside?: boolean;
}

const PopoverMenu = ({
  isOpen,
  onOpenChange,
  children,
  contentClassName,
  trigger,
  renderAnchor,
  disableClickOutside,
}: PopoverMenuProps) => {
  const triggerElement = (
    <Popover.Trigger asChild>
      {trigger}
    </Popover.Trigger>
  );

  return (
    <Popover.Root open={isOpen} onOpenChange={onOpenChange}>
      {renderAnchor
        ? (
            <Popover.Anchor asChild>
              {renderAnchor(triggerElement) as ReactElement}
            </Popover.Anchor>
          )
        : triggerElement}
      <Popover.Portal>
        <>
          <div
            className="fixed inset-0 bg-black/60 z-50"
            role="button"
            tabIndex={0}
            onClick={disableClickOutside ? undefined : () => onOpenChange(false)}
            onKeyDown={(e) => {
              if (!disableClickOutside && (e.key === 'Enter' || e.key === ' ')) {
                onOpenChange(false);
              }
            }}
          />
          <Popover.Content
            side="bottom"
            align="center"
            sideOffset={8}
            onPointerDownOutside={disableClickOutside ? e => e.preventDefault() : undefined}
            onInteractOutside={disableClickOutside ? e => e.preventDefault() : undefined}
            className={cn('rounded-2xl w-full px-2 py-4 bg-violet-main-800 text-white z-60', contentClassName)}
          >
            {children}
          </Popover.Content>
        </>
      </Popover.Portal>
    </Popover.Root>
  );
};

export default PopoverMenu;
